import { Input } from '@/components/ui/input';
import { cn } from '@/lib/cn';
import { ChevronDown, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

interface Option {
  id: string;
  label: string;
}

interface MultiSelectFilterProps {
  options: Option[];
  selected: string[];
  onChange: (ids: string[]) => void;
  /** 드롭다운 안 검색 input placeholder. */
  placeholder: string;
  /** 선택 없음일 때 트리거에 표시. */
  triggerPlaceholder: string;
  triggerClassName?: string;
  /**
   * 제공되면 검색을 호출부에 위임 — 클라이언트 substring 필터링을 끄고 options 를
   * 그대로 표시. 호출부가 디바운스 + 서버 검색으로 채워준 옵션이 그대로 노출된다.
   * 사용자 풀이 LIMIT 200 을 넘어서 클라이언트 필터로는 못 찾는 경우 등에 사용.
   */
  onSearchChange?: (q: string) => void;
  /**
   * 무한 스크롤 — sentinel 이 화면에 들어오면 호출된다. hasMore=true 이고 isLoadingMore=false
   * 일 때만 트리거. onSearchChange 와 함께 쓰면 서버 검색 + 페이지네이션 조합.
   */
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  /**
   * 이미 선택된 옵션이지만 현재 options (서버 검색/페이지네이션 결과) 에 없을 수 있는 항목.
   * 호출부가 선택 시점의 label 을 캐싱해 union 으로 넘긴다. 라벨 보존 + 트리거 라벨 결정에 사용.
   */
  selectedExtras?: Option[];
}

/**
 * UsageStats / ChatLog 에 동일 복제돼있던 MultiSelectFilter 의 shared 단일 본.
 * fixed-position 드롭다운 + 검색 + 체크박스 멀티 선택. 선택 1개 시 라벨, 2개+ 시 "A 외 N".
 */
export function MultiSelectFilter({
  options,
  selected,
  onChange,
  placeholder,
  triggerPlaceholder,
  triggerClassName,
  onSearchChange,
  onLoadMore,
  hasMore,
  isLoadingMore,
  selectedExtras,
}: MultiSelectFilterProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [rect, setRect] = useState<DOMRect | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  /** 'down' = 트리거 아래로, 'up' = 위로. 트리거 위치 기준 자동 결정. */
  const [placement, setPlacement] = useState<'down' | 'up'>('down');

  /** 드롭다운 추정 높이 (검색 input + max-h-48 = ~232px). */
  const ESTIMATED_DROPDOWN_HEIGHT = 240;

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!triggerRef.current?.contains(t) && !dropdownRef.current?.contains(t)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  // 선택된 항목의 라벨 lookup — selectedExtras 우선, options fallback.
  // 서버 페이지네이션/검색으로 options 가 좁혀져도 트리거 라벨이 사라지지 않도록.
  const labelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const o of options) map.set(o.id, o.label);
    if (selectedExtras) for (const o of selectedExtras) map.set(o.id, o.label);
    return map;
  }, [options, selectedExtras]);

  // 드롭다운 표시 옵션 — selectedExtras 의 항목 중 options 에 없는 것을 앞에 끼워 넣어
  // 선택된 사용자가 검색/페이지 결과에 없더라도 체크박스 상태가 보이게 한다.
  const displayOptions = useMemo(() => {
    if (!selectedExtras?.length) return options;
    const existing = new Set(options.map((o) => o.id));
    const extras = selectedExtras.filter((o) => !existing.has(o.id) && selected.includes(o.id));
    return extras.length > 0 ? [...extras, ...options] : options;
  }, [options, selectedExtras, selected]);

  const filtered = useMemo(() => {
    // 서버 검색 위임 모드: 호출부가 options 를 이미 필터해서 넘긴다.
    if (onSearchChange) return displayOptions;
    return search
      ? displayOptions.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()))
      : displayOptions;
  }, [search, displayOptions, onSearchChange]);
  const toggle = (id: string) =>
    onChange(selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id]);

  const triggerLabel =
    selected.length === 0
      ? triggerPlaceholder
      : selected.length === 1
        ? (labelById.get(selected[0] ?? '') ?? triggerPlaceholder)
        : `${labelById.get(selected[0] ?? '') ?? ''} 외 ${selected.length - 1}`;

  // 무한 스크롤 sentinel — 드롭다운이 열려있고 onLoadMore 가 있을 때만 observer 설정.
  // hasMore/isLoadingMore 가 바뀔 때마다 콜백 자체는 변하지 않지만, 콜백 안에서 ref 로 최신
  // 값 참조하도록 useRef 로 보관 — observer 를 재생성하지 않아 깜빡임/중복 호출을 줄임.
  const loadMoreStateRef = useRef({ onLoadMore, hasMore, isLoadingMore });
  loadMoreStateRef.current = { onLoadMore, hasMore, isLoadingMore };

  useEffect(() => {
    if (!open || !onLoadMore) return;
    const sentinel = sentinelRef.current;
    const root = scrollContainerRef.current;
    if (!sentinel || !root) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const s = loadMoreStateRef.current;
        if (entries[0]?.isIntersecting && s.hasMore && !s.isLoadingMore) {
          s.onLoadMore?.();
        }
      },
      { root, rootMargin: '80px', threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [open, onLoadMore]);

  return (
    // min-w-0 — flex 부모 안에서 트리거가 컨텐츠 길이에 따라 부풀지 않도록.
    <div className="relative min-w-0">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => {
          if (!open && triggerRef.current) {
            const r = triggerRef.current.getBoundingClientRect();
            setRect(r);
            const spaceBelow = window.innerHeight - r.bottom;
            const spaceAbove = r.top;
            setPlacement(
              spaceBelow < ESTIMATED_DROPDOWN_HEIGHT && spaceAbove > spaceBelow ? 'up' : 'down',
            );
          }
          setOpen((v) => !v);
        }}
        className={cn(
          'flex items-center gap-1 w-full h-8 rounded-md border border-border bg-surface px-2 text-xs hover:bg-surface-muted transition-colors cursor-pointer',
          selected.length > 0 ? 'text-foreground' : 'text-foreground-muted',
          triggerClassName,
        )}
      >
        <span className="truncate flex-1 text-left">{triggerLabel}</span>
        {selected.length > 0 ? (
          // biome-ignore lint/a11y/useSemanticElements: nested button 회피
          <span
            role="button"
            tabIndex={0}
            aria-label="선택 해제"
            title="선택 해제"
            onClick={(e) => {
              e.stopPropagation();
              onChange([]);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                onChange([]);
              }
            }}
            className="shrink-0 p-0.5 rounded hover:bg-foreground/10 text-foreground-muted hover:text-foreground cursor-pointer"
          >
            <X className="size-3" />
          </span>
        ) : null}
        <ChevronDown
          className={cn(
            'size-3.5 shrink-0 text-foreground-muted transition-transform',
            open && 'rotate-180',
          )}
        />
      </button>
      {open && rect ? (
        <div
          ref={dropdownRef}
          style={{
            position: 'fixed',
            ...(placement === 'up'
              ? { bottom: window.innerHeight - rect.top + 4 }
              : { top: rect.bottom + 4 }),
            left: rect.left,
            width: rect.width,
            zIndex: 9999,
          }}
          className="rounded-md border border-border bg-surface shadow-lg"
        >
          <div className="p-2 border-b border-border">
            <Input
              placeholder={placeholder}
              value={search}
              onChange={(e) => {
                const v = e.target.value;
                setSearch(v);
                onSearchChange?.(v);
              }}
              className="h-7 text-xs"
              autoFocus
            />
          </div>
          <div ref={scrollContainerRef} className="max-h-48 overflow-y-auto divide-y divide-border">
            {filtered.length === 0 ? (
              <p className="px-3 py-2 text-xs text-foreground-muted">
                {isLoadingMore ? '불러오는 중…' : '검색 결과 없음'}
              </p>
            ) : (
              <>
                {filtered.map((o) => (
                  <label
                    key={o.id}
                    className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-surface-muted"
                  >
                    <input
                      type="checkbox"
                      checked={selected.includes(o.id)}
                      onChange={() => toggle(o.id)}
                      className="size-3.5 rounded border-border shrink-0"
                    />
                    <span className="truncate">{o.label}</span>
                  </label>
                ))}
                {onLoadMore ? (
                  <div
                    ref={sentinelRef}
                    className="px-3 py-2 text-center text-xs text-foreground-muted"
                  >
                    {isLoadingMore ? '불러오는 중…' : hasMore ? '' : '— 끝 —'}
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
